import { IBEError } from '@common/interfaces/be-error.interface';
import { ICollectionRequestOptions } from '@common/interfaces/collection-request-options.interface';
import { ICollection } from '@common/interfaces/collection.interface';
import { IInfiniteCollection } from '@common/interfaces/infinite-collection.interface';
import { UseQueryOptions } from '@common/interfaces/query-options.interface';
import { UID } from '@common/interfaces/uid.interface';
import { createCollection } from '@common/utils/create-collection.util';
import { customNotify, IErrorItem } from '@common/utils/custom-notify.util';
import {
    EItemV1OrderGet_Ordering,
    IOrder,
    IOrderList,
    itemApiRequest,
    TItemV1InvoiceIdGetError,
    TItemV1OrderGetError,
    TItemV1OrderIdGetError,
} from '@src/api/item-generated-api';
import { createOrderDto, IOrderDto } from '@src/api/model/order.dto';
import { queryClient } from '@src/shared/query-client';
import { useEffect } from 'react';
import {
    useInfiniteQuery,
    UseInfiniteQueryResult,
    useMutation,
    UseMutationResult,
    useQuery,
    UseQueryResult,
} from 'react-query';
import { apiHooks } from '../api-hooks';

const NAME = 'orders';

const useGetInfiniteOrderList = (
    getCollectionOptions: () => ICollectionRequestOptions,
    queryOption?: UseQueryOptions,
): UseInfiniteQueryResult<ICollection<IOrderDto>, IBEError<TItemV1OrderGetError>> => {
    const queries = [];
    const productQuery = apiHooks.packHouse.useGetProducts();
    queries.push(productQuery);

    const dcQuery = apiHooks.packHouse.useGetDeliveryConfigurationList({ limit: 1000 }, {});
    queries.push(dcQuery);

    const QUERY_KEY = `${NAME}-list-infinite`;
    const result = useInfiniteQuery<{ temp?: IOrderList; next_page?: number }, IBEError<TItemV1OrderGetError>>(
        QUERY_KEY,
        ({ pageParam }) => {
            const collectionOptions = getCollectionOptions();
            return itemApiRequest
                .itemV1OrderGet(
                    {
                        limit: collectionOptions.limit,
                        page: pageParam?.next_page || 0,
                        sort_by: 'order_date',
                        ordering: EItemV1OrderGet_Ordering.descending,
                    },
                    {},
                )
                .then((r) => {
                    return createCollection(
                        r.data.order_list?.map((c) => createOrderDto(c)) || [],
                        r.data.next_page || 0,
                        collectionOptions,
                    );
                });
        },
        {
            enabled: queries.every((q) => q.isSuccess) && (queryOption?.enabled !== undefined ? queryOption?.enabled : true),
            getNextPageParam: (lastPage) => lastPage.next_page,
        },
    );

    const errors = queries.map((q) => ({ beError: q.error as IBEError, onShown: q.remove } as IErrorItem));
    useEffect(() => {
        customNotify.error(errors);
    }, [errors]);

    return {
        ...result,
        isFetching: queries.some((q) => q.isFetching || q.isLoading) || result.isFetching,
    } as unknown as ReturnType<typeof useGetInfiniteOrderList>;
};

const useGetOrderById = (
    id: UID,
    queryOptions?: UseQueryOptions,
): UseQueryResult<IOrderDto, IBEError<TItemV1OrderIdGetError>> => {
    const {
        error: productError,
        isFetched: productFetched,
        remove: productRemove,
    } = apiHooks.packHouse.useGetProducts();

    const {
        error: deliveryConfigsListError,
        isFetched: deliveryConfigsFetched,
        remove: deliveryConfigsRemove,
    } = apiHooks.packHouse.useGetDeliveryConfigurationList({ limit: 1000 }, {});

    useEffect(() => {
        customNotify.error([
            { beError: productError, onShown: productRemove },
            { beError: deliveryConfigsListError, onShown: deliveryConfigsRemove },
        ]);
    }, [deliveryConfigsRemove, deliveryConfigsListError, productError, productRemove]);

    return useQuery<IOrderDto, IBEError<TItemV1OrderIdGetError>>(
        `${NAME}-by-id-${id}`,
        () => {
            return itemApiRequest.itemV1OrderIdGet(id, {}).then((res) => createOrderDto(res.data.order as IOrder));
        },
        {
            ...queryOptions,
            enabled:
                productFetched &&
                deliveryConfigsFetched &&
                (queryOptions?.enabled !== undefined ? queryOptions.enabled : true),
        },
    );
};

const useGetOrderInvoiceById = (
    id: UID,
    queryOptions?: UseQueryOptions,
): UseMutationResult<string, IBEError<TItemV1InvoiceIdGetError>, void> => {
    return useMutation(
        () => {
            return itemApiRequest.itemV1InvoiceIdGet(id, {}).then((res) => res.data.download_url as string);
        },
        {
            ...queryOptions,
            mutationKey: `${NAME}-invoice-by-id-${id}`,
        },
    );
};

const useGetInfiniteOrderListFromCache = (): IInfiniteCollection<IOrderDto> | undefined => {
    return queryClient.getQueryData(`${NAME}-list-infinite`);
};

export const orders = {
    useGetOrderById,
    useGetOrderInvoiceById,
    useGetInfiniteOrderList,
    useGetInfiniteOrderListFromCache,
};
